import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Collapse, Typography } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { colors } from '../theme';

// Shared "How to read this" pattern used across the depletions
// dashboard. Each section has its own visual language (chips, badges,
// mini-bars, status dots), so the explainer just provides the shell —
// the toggle, the card, the responsive grid — and the calling section
// supplies its own row content with sample swatches.
//
// Default behavior is **collapsed**: a small "How to read this" link
// in the section, click to expand the card inline. Veteran reps never
// see the explainer once they know the section; new reps are one
// click away from a full key.

export interface SectionExplainerRow {
  /** Small visual sample — should mirror the actual element in the
   *  section's chart/table (e.g. a styled chip, a mini bar, an icon). */
  sample: ReactNode;
  /** Short bold heading for the element. */
  term: string;
  /** Plain-English description. Can include <strong>, <em>, etc. */
  desc: ReactNode;
}

interface SectionExplainerProps {
  rows: SectionExplainerRow[];
  /** Optional override for the toggle copy. Defaults to "How to read this". */
  label?: string;
  /** Override the initial collapsed state if you want a section to
   *  default to open (rare — use sparingly). */
  defaultOpen?: boolean;
  /** Horizontal alignment of the collapsed toggle pill. Defaults to left. */
  align?: 'left' | 'right';
}

export function SectionExplainer({
  rows,
  label = 'How to read this',
  defaultOpen = false,
  align = 'left',
}: SectionExplainerProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        <Box
          component="button"
          type="button"
          onClick={() => setOpen((v) => !v)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.6,
            px: 1.25,
            py: 0.6,
            border: `1px solid ${open ? `${colors.chartPrimary}40` : colors.border}`,
            background: open ? `${colors.chartPrimary}0a` : '#fff',
            cursor: 'pointer',
            color: open ? colors.chartPrimary : colors.textMuted,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            borderRadius: 999,
            boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
            transition: 'color 0.15s, background 0.15s, border-color 0.15s',
            '&:hover': {
              color: colors.chartPrimary,
              borderColor: `${colors.chartPrimary}66`,
              background: `${colors.chartPrimary}0a`,
            },
            '&:focus-visible': { outline: `2px solid ${colors.chartPrimary}`, outlineOffset: 2 },
          }}
        >
          <HelpOutlineOutlinedIcon sx={{ fontSize: 14 }} />
          {label}
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 15,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            mt: 1,
            p: 2,
            borderRadius: 2,
            bgcolor: '#fff',
            border: `1px solid ${colors.borderLight}`,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
              columnGap: 3,
              rowGap: 1.25,
            }}
          >
            {rows.map((row, i) => (
              <GuideRow key={i} sample={row.sample} term={row.term}>
                {row.desc}
              </GuideRow>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

interface GuideRowProps {
  sample: ReactNode;
  term: string;
  children: ReactNode;
}

function GuideRow({ sample, term, children }: GuideRowProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
      <Box
        sx={{
          width: 72,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // Clip any sample whose intrinsic width exceeds the slot
          // — a safety net so a wide chip can never push into the
          // term text and break the row layout.
          overflow: 'hidden',
          mt: 0.1,
        }}
      >
        {sample}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 11.5,
            fontWeight: 700,
            color: colors.textPrimary,
            lineHeight: 1.3,
          }}
        >
          {term}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: colors.textMuted,
            lineHeight: 1.5,
            mt: 0.15,
            '& strong': { color: colors.textSecondary, fontWeight: 700 },
            '& em': { color: colors.textSecondary, fontStyle: 'italic' },
          }}
        >
          {children}
        </Typography>
      </Box>
    </Box>
  );
}
